import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import banner from "./images/banner.png";
import buttonAndroid from "./images/button-android.png";
import buttonApple from "./images/button-apple.png";
import "./styles.css";

const APP_STORE = "https://apps.apple.com/id/app/diary-muslim/id1611557968";
const PLAY_STORE = "https://play.google.com/store/apps/details?id=com.diarymuslim";

const FLOW = [
  { n: "01", t: "Baca & Dengar", d: "Buka mushaf digital, baca ayat per ayat atau dengarkan murottal dari qari pilihan — kapan pun, di mana pun." },
  { n: "02", t: "Tandai & Catat", d: "Simpan surah dan ayat favorit, lalu tulis refleksi: pelajaran apa yang Allah sampaikan untukmu hari ini." },
  { n: "03", t: "Bagikan ke Feed", d: "Posting refleksimu ke home feed. Teman dan majelismu bisa membaca, menyukai, dan mengomentari." },
  { n: "04", t: "Ikut Challenge & Majelis", d: "Jaga ritme tilawah lewat challenge harian dan grup majelis yang saling mengingatkan dan menyemangati." },
  { n: "05", t: "Raih Badge & Khatam", d: "Kumpulkan poin dan badge di profil, pantau grafik progres, dan lanjutkan perjalanan menuju khatam." },
];

const FEATURES = [
  { icon: "M", t: "Mushaf Digital Resmi", d: "114 surah dan 30 juz dengan Mushaf Standar Indonesia dan Terjemahan Bahasa Indonesia Edisi 2019 — dikonsultasikan bersama LPMQ Kemenag RI.", tag: "Baca" },
  { icon: "A", t: "Murottal Per Ayat", d: "Dengarkan bacaan dari para qari (reciter) untuk tartil dan tadabbur. Cocok untuk perjalanan, kerja, maupun waktu luang.", tag: "Dengar" },
  { icon: "F", t: "Feed & Refleksi Harian", d: "Ubah bacaan jadi catatan hidup. Tulis refleksi ayat, bagikan ke feed, dan terima apresiasi serta komentar yang menguatkan.", tag: "Berbagi" },
  { icon: "C", t: "Challenge Tilawah", d: "One Day One Juz, One Day One Verse, dan Challenge Surah — target harian yang jelas dengan leaderboard dan komentar peserta.", tag: "Istiqomah" },
  { icon: "J", t: "Majelis Komunitas", d: "Buat atau gabung majelis bersama keluarga, teman, atau komunitas pengajian. Ada ranking anggota untuk saling menyemangati.", tag: "Komunitas" },
  { icon: "P", t: "Progres & Khatam", d: "Grafik progres baca/dengar, penanda bacaan terakhir (continue reading), favorit surah-ayat, serta badge dan poin di profilmu.", tag: "Progres" },
];

const CHALLENGES = [
  { t: "One Day One Juz", s: "Tantangan Harian Juz", d: "Satu juz setiap hari selama 30 hari — dari Juz 1 di Day-1 hingga Juz 30 di Day-30, berurutan menuju khatam.", poin: "10 poin / hari", total: "maks. 300 poin", to: "/term-challenge-juz" },
  { t: "One Day One Verse", s: "Tantangan Harian Ayat", d: "Mulai dari yang ringan: satu ayat setiap hari, berurutan sejak Day-1. Kecil, konsisten, dan menenangkan.", poin: "1 poin / hari", total: "30 poin / 30 hari", to: "/term-challenge-verse" },
  { t: "Challenge Surah", s: "Tantangan Per Surah", d: "Fokus menuntaskan surah pilihan sesuai periode program. Pas untuk target personal maupun majelis.", poin: "Sesuai program", total: "Badge finisher", to: "/term-challenge-surah" },
];

const FAQS = [
  { q: "Apakah Diary Muslim gratis?", a: "Ya, aplikasi Diary Muslim dapat diunduh gratis di App Store dan Google Play. Buat akun dengan email, Google, atau Apple untuk mulai membaca dan bergabung dengan komunitas." },
  { q: "Mushaf apa yang digunakan?", a: "Diary Muslim menggunakan Mushaf Standar Indonesia dan Terjemahan Bahasa Indonesia Edisi 2019. Pengembangannya dikonsultasikan dengan Forum Pelayan Al-Qur'an dan Lajnah Pentashihan Mushaf Al-Qur'an (LPMQ) Kemenag RI." },
  { q: "Bagaimana cara kerja poin dan badge?", a: "Setiap hari kamu menyelesaikan target challenge, kamu mendapat poin (mis. 10 poin/hari untuk One Day One Juz). Peserta yang tuntas sesuai target waktu meraih Badge Emas, dan yang tuntas di luar target waktu meraih Badge Perak — keduanya tampil di profil. Ketentuan lengkap ada di halaman Ketentuan Challenge." },
  { q: "Bisakah saya menghapus akun?", a: "Bisa. Buka Pengaturan (ikon roda gigi) di tab Profil aplikasi, pilih Request Delete Account, lalu selesaikan verifikasi kode yang dikirim ke email. Panduan lengkapnya tersedia di halaman Hapus Akun." },
];

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(0);
  const [faqVisible, setFaqVisible] = useState(false);
  const faqRef = useRef(null);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setFaqVisible(true);
      return undefined;
    }
    const root = faqRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { setFaqVisible(true); io.disconnect(); }
      }),
      { threshold: 0.1 }
    );
    io.observe(root);
    return () => io.disconnect();
  }, []);
  return (
    <div className="lp">
      {/* HERO */}
      <section className="hero">
        <div className="site-wrap hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">Mushaf Standar Indonesia • LPMQ Kemenag RI</span>
            <h1>Baca, dengar, dan bagikan kebaikan <span className="hl">Al-Qur&rsquo;an</span> setiap hari.</h1>
            <p className="hero-sub">Diary Muslim adalah aplikasi Al-Qur&rsquo;an digital pertama berbasis <strong>social media engagement</strong>: tilawah dan murottal yang terhubung dengan feed refleksi, challenge harian, dan majelis — agar kamu tidak mengaji sendirian.</p>
            <div className="store-row">
              <a href={APP_STORE} target="_blank" rel="noopener noreferrer"><img src={buttonApple} alt="Download di App Store" /></a>
              <a href={PLAY_STORE} target="_blank" rel="noopener noreferrer"><img src={buttonAndroid} alt="Download di Google Play" /></a>
            </div>
            <ul className="hero-points">
              <li><strong>114</strong> Surah</li><li><strong>30</strong> Juz</li><li><strong>6.236</strong> Ayat</li><li>Gratis di iOS &amp; Android</li>
            </ul>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="phone-frame"><img src={banner} alt="" className="phone-img" /></div>
            <div className="float-card fc-progress"><span className="fc-label">Progres Khatam</span><div className="bar"><i style={{ width: "68%" }} /></div><span className="fc-value">Juz 21 • 68%</span></div>
            <div className="float-card fc-challenge"><span className="fc-label">One Day One Juz</span><span className="fc-value">Day 12 dari 30 • +10 poin hari ini</span></div>
            <div className="float-card fc-feed"><span className="fc-label">Feed Refleksi</span><span className="fc-value">&ldquo;Ayat ini menenangkan sekali hari ini…&rdquo;</span></div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="trust">
        <div className="site-wrap trust-row">
          <span>Dikonsultasikan bersama <strong>LPMQ Kemenag RI</strong></span>
          <span><strong>Forum Pelayan Al-Qur&rsquo;an</strong> Mulia</span>
          <span>Terjemahan <strong>Edisi 2019</strong></span>
          <span><strong>PT Diary Muslim</strong> Infomedia</span>
        </div>
      </section>

      {/* FLOW */}
      <section className="section" id="cara-kerja">
        <div className="site-wrap">
          <p className="kicker">Cara Kerja</p>
          <h2 className="section-title">Dari tilawah pribadi, menjadi kebiasaan yang dijaga bersama</h2>
          <p className="section-sub">Inilah alur utama Diary Muslim — setiap bacaan tercatat, terasa manfaatnya, dan dijaga konsistensinya oleh komunitas.</p>
          <div className="flow-grid">
            {FLOW.map((s) => (
              <article className="flow-card" key={s.n}><span className="flow-num">{s.n}</span><h3>{s.t}</h3><p>{s.d}</p></article>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section alt" id="fitur">
        <div className="site-wrap">
          <p className="kicker">Fitur Utama</p>
          <h2 className="section-title">Semua yang kamu butuhkan untuk dekat dengan Al-Qur&rsquo;an</h2>
          <p className="section-sub">Bukan sekadar aplikasi baca — Diary Muslim merancang pengalaman sosial yang membuatmu kembali membuka mushaf setiap hari.</p>
          <div className="feat-grid">
            {FEATURES.map((f) => (
              <article className="feat-card" key={f.t}>
                <span className="feat-icon">{f.icon}</span>
                <span className="feat-tag">{f.tag}</span>
                <h3>{f.t}</h3><p>{f.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGE */}
      <section className="section" id="challenge">
        <div className="site-wrap">
          <p className="kicker">Challenge Tilawah</p>
          <h2 className="section-title">Target harian yang jelas, badge yang membanggakan</h2>
          <p className="section-sub">Pilih ritmemu — satu juz, satu ayat, atau satu surah. Setiap kehadiran tercatat sebagai poin, dan finisher menerima badge di profil.</p>
          <div className="ch-grid">
            {CHALLENGES.map((c) => (
              <article className="ch-card" key={c.t}>
                <span className="ch-sub">{c.s}</span>
                <h3>{c.t}</h3><p>{c.d}</p>
                <div className="ch-meta"><span>{c.poin}</span><span>{c.total}</span></div>
                <Link className="text-link" to={c.to}>Lihat ketentuan →</Link>
              </article>
            ))}
          </div>
          <div className="badge-note">
            <div><h3>Badge Emas</h3><p>Tuntas 100% sesuai target waktu program — misalnya 30 juz dalam 30 hari.</p></div>
            <div><h3>Badge Perak</h3><p>Tetap tuntas membaca seluruh target, meski di luar jadwal ideal program.</p></div>
            <Link className="btn-outline" to="/termchallenge">Semua Ketentuan Challenge</Link>
          </div>
        </div>
      </section>

      {/* MAJELIS / SOCIAL */}
      <section className="section dark" id="majelis">
        <div className="site-wrap social-grid">
          <div>
            <p className="kicker light">Majelis &amp; Feed Sosial</p>
            <h2 className="section-title light">Mengaji itu menenangkan. Mengaji bersama, menguatkan.</h2>
            <p className="section-sub light">Buat majelis untuk keluarga, sahabat, atau komunitas pengajianmu. Setiap anggota terlihat progresnya di ranking majelis — saling menyemangati tanpa merasa digurui.</p>
            <ul className="check-list">
              <li>Buat majelis privat dan undang pengikutmu</li>
              <li>Ranking anggota berdasarkan konsistensi tilawah</li>
              <li>Feed refleksi dengan like dan komentar yang positif</li>
              <li>Ikuti (follow) sahabat dan lihat aktivitas bacaan mereka</li>
            </ul>
            <a className="btn-gold" href={PLAY_STORE} target="_blank" rel="noopener noreferrer">Buat Majelis Pertamamu — Gratis</a>
          </div>
          <div className="social-cards" aria-hidden="true">
            <div className="mini-card"><span className="mc-head">Majelis Keluarga Wijaya</span><span className="mc-row">Ranking #1 minggu ini — 28 hari berturut-turut</span><span className="mc-row">Anggota aktif: 12 orang</span></div>
            <div className="mini-card"><span className="mc-head">Feed Hari Ini</span><span className="mc-row">&ldquo;QS. Al-Insyirah: 6 — sesudah kesulitan itu ada kemudahan.&rdquo;</span><span className="mc-row muted">24 suka • 8 komentar</span></div>
            <div className="mini-card"><span className="mc-head">Pengingat Tilawah</span><span className="mc-row">Sudah baca hari ini? Lanjutkan Juz 21, Ayat 45.</span></div>
          </div>
        </div>
      </section>

      {/* STEPS + FAQ */}
      <section className="section">
        <div className="site-wrap">
          <p className="kicker">Mulai Sekarang</p>
          <h2 className="section-title">Tiga langkah menuju tilawah yang istiqomah</h2>
          <div className="steps-row">
            <div className="step"><span>1</span><h3>Download Gratis</h3><p>Unduh Diary Muslim di App Store atau Google Play, lalu daftar dengan email, Google, atau Apple.</p></div>
            <div className="step"><span>2</span><h3>Pilih Targetmu</h3><p>Mulai dari One Day One Verse, ikut challenge, atau gabung majelis yang sudah ada.</p></div>
            <div className="step"><span>3</span><h3>Baca, Tulis, Bagikan</h3><p>Tilawah hari ini, tulis refleksinya, dan biarkan komunitas menjagamu tetap konsisten.</p></div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="site-wrap faq-inner" ref={faqRef}>
          <div className={`faq-head${faqVisible ? " in" : ""}`}>
            <p className="kicker">FAQ</p>
            <h2 className="section-title">Pertanyaan yang sering diajukan</h2>
            <p className="section-sub">Semua yang perlu Anda ketahui sebelum memulai perjalanan tilawah bersama Diary Muslim.</p>
          </div>
          <div className="faq-list">
            {FAQS.map((f, i) => (
              <div className={`faq-item${openFaq === i ? " is-open" : ""}${faqVisible ? " in" : ""}`} key={f.q}>
                <button
                  className="faq-q"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                >
                  <span className="faq-num">0{i + 1}</span>
                  <span className="faq-label">{f.q}</span>
                  <span className="faq-plus" aria-hidden="true" />
                </button>
                <div className="faq-a" aria-hidden={openFaq !== i}>
                  <div className="faq-a-inner"><p>{f.a}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta">
        <div className="site-wrap cta-inner">
          <h2>Perjalanan khatammu dimulai dari satu ayat hari ini.</h2>
          <p>Download Diary Muslim sekarang — gratis untuk iOS &amp; Android.</p>
          <div className="store-row center">
            <a href={APP_STORE} target="_blank" rel="noopener noreferrer"><img src={buttonApple} alt="Download di App Store" /></a>
            <a href={PLAY_STORE} target="_blank" rel="noopener noreferrer"><img src={buttonAndroid} alt="Download di Google Play" /></a>
          </div>
          <span className="cta-note">Read, listen, and share love everyday.</span>
        </div>
      </section>
    </div>
  );
}
